// The template gallery: the cards on the start screen, Skills and Agents (PL-14).
//
// Namespace `templates`; owner: ui/workspace/company-feed.tsx (TemplateGallery, TemplateCard).
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// `{name}` is a template's or Skill draft's name; `{apps}` a list of apps the
// template reads, already joined for the locale ("Fortnox and Gmail").
// `needs` and `uses` agree with the template, never with the apps, so one form
// fits one app or several; languages that decline the object use "Label: {apps}".
// "Skill" stays the product noun, as in the other namespaces.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  heading: 'Start from a template',
  description: 'Each one becomes a private Skill draft. Change it in chat, then publish.',
  useTemplate: 'Use template: {name}',
  creating: 'Creating draft…',
  needs: 'Needs {apps}',
  uses: 'Uses {apps}',
  draftCreated: 'Skill draft “{name}” created. Change it in chat, then publish.',
  draftFailed: 'Could not create the Skill draft. Try again.',
  agentDescription:
    'Each one becomes a private agent, switched off until you’re ready. Try it, then turn it on.',
  agentDraftCreated: 'Agent “{name}” created, switched off. Try it, then turn it on.',
  agentDraftFailed: 'Could not create the agent. Try again.',
  scheduleStartsWhenOn: 'starts when on',
};

export type TemplatesCopyKey = keyof typeof en;
type Copy = Record<TemplatesCopyKey, string>;

const bg: Copy = {
  heading: 'Започнете от шаблон',
  description:
    'Всеки шаблон става лична чернова на Skill. Променете я в чата, след това я публикувайте.',
  useTemplate: 'Използване на шаблона: {name}',
  creating: 'Създаване на черновата…',
  needs: 'Изисква {apps}',
  uses: 'Използва {apps}',
  draftCreated:
    'Черновата на Skill „{name}“ е създадена. Променете я в чата, след това я публикувайте.',
  draftFailed: 'Черновата на Skill не можа да бъде създадена. Опитайте отново.',
  agentDescription:
    'Всеки шаблон става личен агент, изключен, докато не сте готови. Изпробвайте го, след това го включете.',
  agentDraftCreated:
    'Агентът „{name}“ е създаден, изключен. Изпробвайте го, след това го включете.',
  agentDraftFailed: 'Агентът не можа да бъде създаден. Опитайте отново.',
  scheduleStartsWhenOn: 'стартира, когато е включен',
};

const cs: Copy = {
  heading: 'Začněte od šablony',
  description:
    'Z každé šablony vznikne soukromý koncept Skillu. Upravte ho v chatu a pak zveřejněte.',
  useTemplate: 'Použít šablonu: {name}',
  creating: 'Vytváření konceptu…',
  needs: 'Vyžaduje {apps}',
  uses: 'Používá {apps}',
  draftCreated: 'Koncept Skillu „{name}“ byl vytvořen. Upravte ho v chatu a pak zveřejněte.',
  draftFailed: 'Koncept Skillu se nepodařilo vytvořit. Zkuste to znovu.',
  agentDescription:
    'Z každé šablony vznikne soukromý agent, vypnutý, dokud nebudete připraveni. Vyzkoušejte ho a pak ho zapněte.',
  agentDraftCreated: 'Agent „{name}“ byl vytvořen a je vypnutý. Vyzkoušejte ho a pak ho zapněte.',
  agentDraftFailed: 'Agenta se nepodařilo vytvořit. Zkuste to znovu.',
  scheduleStartsWhenOn: 'spustí se po zapnutí',
};

const da: Copy = {
  heading: 'Start fra en skabelon',
  description:
    'Hver skabelon bliver en privat Skill-kladde. Ret den i chatten, og udgiv den derefter.',
  useTemplate: 'Brug skabelonen: {name}',
  creating: 'Opretter kladde…',
  needs: 'Kræver {apps}',
  uses: 'Bruger {apps}',
  draftCreated: 'Skill-kladden “{name}” er oprettet. Ret den i chatten, og udgiv den derefter.',
  draftFailed: 'Skill-kladden kunne ikke oprettes. Prøv igen.',
  agentDescription:
    'Hver skabelon bliver en privat agent, der er slået fra, til du er klar. Prøv den, og slå den derefter til.',
  agentDraftCreated:
    'Agenten “{name}” er oprettet og er slået fra. Prøv den, og slå den derefter til.',
  agentDraftFailed: 'Agenten kunne ikke oprettes. Prøv igen.',
  scheduleStartsWhenOn: 'starter, når den er slået til',
};

const de: Copy = {
  heading: 'Mit einer Vorlage beginnen',
  description:
    'Jede Vorlage wird zu einem privaten Skill-Entwurf. Passen Sie ihn im Chat an und veröffentlichen Sie ihn dann.',
  useTemplate: 'Vorlage verwenden: {name}',
  creating: 'Entwurf wird erstellt…',
  needs: 'Benötigt {apps}',
  uses: 'Nutzt {apps}',
  draftCreated:
    'Skill-Entwurf „{name}“ erstellt. Passen Sie ihn im Chat an und veröffentlichen Sie ihn dann.',
  draftFailed: 'Der Skill-Entwurf konnte nicht erstellt werden. Versuchen Sie es erneut.',
  agentDescription:
    'Jede Vorlage wird zu einem privaten Agenten, der ausgeschaltet bleibt, bis Sie bereit sind. Testen Sie ihn und schalten Sie ihn dann ein.',
  agentDraftCreated:
    'Agent „{name}“ erstellt, ausgeschaltet. Testen Sie ihn und schalten Sie ihn dann ein.',
  agentDraftFailed: 'Der Agent konnte nicht erstellt werden. Versuchen Sie es erneut.',
  scheduleStartsWhenOn: 'startet nach dem Einschalten',
};

const el: Copy = {
  heading: 'Ξεκινήστε από ένα πρότυπο',
  description:
    'Κάθε πρότυπο γίνεται ένα ιδιωτικό πρόχειρο Skill. Αλλάξτε το στη συνομιλία και μετά δημοσιεύστε το.',
  useTemplate: 'Χρήση προτύπου: {name}',
  creating: 'Δημιουργία προχείρου…',
  needs: 'Απαιτεί {apps}',
  uses: 'Χρησιμοποιεί {apps}',
  draftCreated:
    'Το πρόχειρο Skill «{name}» δημιουργήθηκε. Αλλάξτε το στη συνομιλία και μετά δημοσιεύστε το.',
  draftFailed: 'Δεν ήταν δυνατή η δημιουργία του προχείρου Skill. Δοκιμάστε ξανά.',
  agentDescription:
    'Κάθε πρότυπο γίνεται ένας ιδιωτικός agent, απενεργοποιημένος μέχρι να είστε έτοιμοι. Δοκιμάστε τον και μετά ενεργοποιήστε τον.',
  agentDraftCreated:
    'Ο agent «{name}» δημιουργήθηκε, απενεργοποιημένος. Δοκιμάστε τον και μετά ενεργοποιήστε τον.',
  agentDraftFailed: 'Δεν ήταν δυνατή η δημιουργία του agent. Δοκιμάστε ξανά.',
  scheduleStartsWhenOn: 'ξεκινά όταν ενεργοποιηθεί',
};

const es: Copy = {
  heading: 'Empieza con una plantilla',
  description:
    'Cada plantilla se convierte en un borrador privado de Skill. Cámbialo en el chat y luego publícalo.',
  useTemplate: 'Usar plantilla: {name}',
  creating: 'Creando el borrador…',
  needs: 'Necesita {apps}',
  uses: 'Usa {apps}',
  draftCreated:
    'Se ha creado el borrador de Skill «{name}». Cámbialo en el chat y luego publícalo.',
  draftFailed: 'No se pudo crear el borrador de Skill. Vuelve a intentarlo.',
  agentDescription:
    'Cada plantilla se convierte en un agente privado, desactivado hasta que estés listo. Pruébalo y luego actívalo.',
  agentDraftCreated: 'Se ha creado el agente «{name}», desactivado. Pruébalo y luego actívalo.',
  agentDraftFailed: 'No se pudo crear el agente. Vuelve a intentarlo.',
  scheduleStartsWhenOn: 'empieza al activarlo',
};

const et: Copy = {
  heading: 'Alusta mallist',
  description:
    'Igast mallist saab privaatne Skilli mustand. Muuda seda vestluses ja seejärel avalda.',
  useTemplate: 'Kasuta malli: {name}',
  creating: 'Mustandi loomine…',
  needs: 'Vajab: {apps}',
  uses: 'Kasutab: {apps}',
  draftCreated: 'Skilli mustand „{name}” on loodud. Muuda seda vestluses ja seejärel avalda.',
  draftFailed: 'Skilli mustandit ei õnnestunud luua. Proovi uuesti.',
  agentDescription:
    'Igast mallist saab privaatne agent, mis on välja lülitatud, kuni oled valmis. Proovi seda ja lülita see siis sisse.',
  agentDraftCreated:
    'Agent „{name}” on loodud ja välja lülitatud. Proovi seda ja lülita see siis sisse.',
  agentDraftFailed: 'Agenti ei õnnestunud luua. Proovi uuesti.',
  scheduleStartsWhenOn: 'käivitub sisselülitamisel',
};

const fi: Copy = {
  heading: 'Aloita mallista',
  description:
    'Jokaisesta mallista tulee yksityinen Skill-luonnos. Muokkaa sitä keskustelussa ja julkaise sitten.',
  useTemplate: 'Käytä mallia: {name}',
  creating: 'Luonnosta luodaan…',
  needs: 'Vaatii: {apps}',
  uses: 'Käyttää: {apps}',
  draftCreated: 'Skill-luonnos ”{name}” luotiin. Muokkaa sitä keskustelussa ja julkaise sitten.',
  draftFailed: 'Skill-luonnoksen luominen epäonnistui. Yritä uudelleen.',
  agentDescription:
    'Jokaisesta mallista tulee yksityinen agentti, joka on pois päältä, kunnes olet valmis. Kokeile sitä ja kytke se sitten päälle.',
  agentDraftCreated:
    'Agentti ”{name}” luotiin, pois päältä. Kokeile sitä ja kytke se sitten päälle.',
  agentDraftFailed: 'Agentin luominen epäonnistui. Yritä uudelleen.',
  scheduleStartsWhenOn: 'käynnistyy, kun se on päällä',
};

const fr: Copy = {
  heading: 'Partir d’un modèle',
  description:
    'Chaque modèle devient un brouillon de Skill privé. Modifiez-le dans le chat, puis publiez-le.',
  useTemplate: 'Utiliser le modèle : {name}',
  creating: 'Création du brouillon…',
  needs: 'Nécessite {apps}',
  uses: 'Utilise {apps}',
  draftCreated: 'Brouillon de Skill « {name} » créé. Modifiez-le dans le chat, puis publiez-le.',
  draftFailed: 'Impossible de créer le brouillon de Skill. Réessayez.',
  agentDescription:
    'Chaque modèle devient un agent privé, désactivé jusqu’à ce que vous soyez prêt. Testez-le, puis activez-le.',
  agentDraftCreated: 'Agent « {name} » créé, désactivé. Testez-le, puis activez-le.',
  agentDraftFailed: 'Impossible de créer l’agent. Réessayez.',
  scheduleStartsWhenOn: 'démarre une fois activé',
};

const ga: Copy = {
  heading: 'Tosaigh ó theimpléad',
  description:
    'Déantar dréacht príobháideach Skill de gach teimpléad. Athraigh sa chomhrá é, ansin foilsigh é.',
  useTemplate: 'Úsáid an teimpléad: {name}',
  creating: 'Dréacht á chruthú…',
  needs: 'Teastaíonn {apps}',
  uses: 'Úsáideann sé {apps}',
  draftCreated: 'Cruthaíodh an dréacht Skill “{name}”. Athraigh sa chomhrá é, ansin foilsigh é.',
  draftFailed: 'Níorbh fhéidir an dréacht Skill a chruthú. Bain triail eile as.',
  agentDescription:
    'Déantar gníomhaire príobháideach de gach teimpléad, múchta go dtí go mbeidh tú réidh. Bain triail as, ansin cas air é.',
  agentDraftCreated: 'Cruthaíodh an gníomhaire “{name}”, múchta. Bain triail as, ansin cas air é.',
  agentDraftFailed: 'Níorbh fhéidir an gníomhaire a chruthú. Bain triail eile as.',
  scheduleStartsWhenOn: 'tosaíonn nuair atá sé air',
};

const hr: Copy = {
  heading: 'Započnite s predloškom',
  description:
    'Svaki predložak postaje privatni nacrt Skilla. Uredite ga u chatu, a zatim objavite.',
  useTemplate: 'Upotrijebi predložak: {name}',
  creating: 'Izrada nacrta…',
  needs: 'Potrebno: {apps}',
  uses: 'Koristi: {apps}',
  draftCreated: 'Nacrt Skilla „{name}” je izrađen. Uredite ga u chatu, a zatim objavite.',
  draftFailed: 'Nacrt Skilla nije moguće izraditi. Pokušajte ponovno.',
  agentDescription:
    'Svaki predložak postaje privatni agent, isključen dok ne budete spremni. Isprobajte ga, a zatim ga uključite.',
  agentDraftCreated: 'Agent „{name}” je izrađen, isključen. Isprobajte ga, a zatim ga uključite.',
  agentDraftFailed: 'Agenta nije moguće izraditi. Pokušajte ponovno.',
  scheduleStartsWhenOn: 'pokreće se kad je uključen',
};

const hu: Copy = {
  heading: 'Kezdje egy sablonnal',
  description:
    'Minden sablonból privát Skill-piszkozat lesz. Módosítsa a csevegésben, majd tegye közzé.',
  useTemplate: 'Sablon használata: {name}',
  creating: 'Piszkozat létrehozása…',
  needs: 'Szükséges: {apps}',
  uses: 'Használja: {apps}',
  draftCreated:
    'A(z) „{name}” Skill-piszkozat elkészült. Módosítsa a csevegésben, majd tegye közzé.',
  draftFailed: 'Nem sikerült létrehozni a Skill-piszkozatot. Próbálja újra.',
  agentDescription:
    'Minden sablonból privát ügynök lesz, amely kikapcsolva marad, amíg készen nem áll. Próbálja ki, majd kapcsolja be.',
  agentDraftCreated: 'A(z) „{name}” ügynök elkészült, kikapcsolva. Próbálja ki, majd kapcsolja be.',
  agentDraftFailed: 'Nem sikerült létrehozni az ügynököt. Próbálja újra.',
  scheduleStartsWhenOn: 'bekapcsolás után indul',
};

const it: Copy = {
  heading: 'Inizia da un modello',
  description:
    'Ogni modello diventa una bozza privata di Skill. Modificala nella chat, poi pubblicala.',
  useTemplate: 'Usa il modello: {name}',
  creating: 'Creazione della bozza…',
  needs: 'Richiede {apps}',
  uses: 'Usa {apps}',
  draftCreated: 'Bozza di Skill “{name}” creata. Modificala nella chat, poi pubblicala.',
  draftFailed: 'Impossibile creare la bozza di Skill. Riprova.',
  agentDescription:
    'Ogni modello diventa un agente privato, spento finché non sei pronto. Provalo, poi accendilo.',
  agentDraftCreated: 'Agente “{name}” creato, spento. Provalo, poi accendilo.',
  agentDraftFailed: 'Impossibile creare l’agente. Riprova.',
  scheduleStartsWhenOn: 'parte quando è acceso',
};

const lt: Copy = {
  heading: 'Pradėkite nuo šablono',
  description:
    'Kiekvienas šablonas tampa privačiu Skill juodraščiu. Pakeiskite jį pokalbyje, tada paskelbkite.',
  useTemplate: 'Naudoti šabloną: {name}',
  creating: 'Kuriamas juodraštis…',
  needs: 'Reikia: {apps}',
  uses: 'Naudoja: {apps}',
  draftCreated: 'Skill juodraštis „{name}“ sukurtas. Pakeiskite jį pokalbyje, tada paskelbkite.',
  draftFailed: 'Nepavyko sukurti Skill juodraščio. Bandykite dar kartą.',
  agentDescription:
    'Kiekvienas šablonas tampa privačiu agentu, išjungtu, kol būsite pasiruošę. Išbandykite jį, tada įjunkite.',
  agentDraftCreated: 'Agentas „{name}“ sukurtas, išjungtas. Išbandykite jį, tada įjunkite.',
  agentDraftFailed: 'Nepavyko sukurti agento. Bandykite dar kartą.',
  scheduleStartsWhenOn: 'paleidžiamas įjungus',
};

const lv: Copy = {
  heading: 'Sāciet ar veidni',
  description:
    'Katra veidne kļūst par privātu Skill melnrakstu. Mainiet to tērzēšanā un pēc tam publicējiet.',
  useTemplate: 'Izmantot veidni: {name}',
  creating: 'Veido melnrakstu…',
  needs: 'Nepieciešams: {apps}',
  uses: 'Izmanto: {apps}',
  draftCreated:
    'Skill melnraksts “{name}” ir izveidots. Mainiet to tērzēšanā un pēc tam publicējiet.',
  draftFailed: 'Neizdevās izveidot Skill melnrakstu. Mēģiniet vēlreiz.',
  agentDescription:
    'Katra veidne kļūst par privātu aģentu, kas ir izslēgts, līdz esat gatavs. Izmēģiniet to un pēc tam ieslēdziet.',
  agentDraftCreated:
    'Aģents “{name}” ir izveidots un izslēgts. Izmēģiniet to un pēc tam ieslēdziet.',
  agentDraftFailed: 'Neizdevās izveidot aģentu. Mēģiniet vēlreiz.',
  scheduleStartsWhenOn: 'sāk darboties pēc ieslēgšanas',
};

const mt: Copy = {
  heading: 'Ibda minn mudell',
  description: 'Kull mudell isir abbozz privat ta’ Skill. Ibdlu fiċ-chat, imbagħad ippubblikah.',
  useTemplate: 'Uża l-mudell: {name}',
  creating: 'Qed jinħoloq l-abbozz…',
  needs: 'Jeħtieġ {apps}',
  uses: 'Juża {apps}',
  draftCreated: 'L-abbozz ta’ Skill “{name}” inħoloq. Ibdlu fiċ-chat, imbagħad ippubblikah.',
  draftFailed: 'Ma setax jinħoloq l-abbozz ta’ Skill. Erġa’ pprova.',
  agentDescription:
    'Kull mudell isir aġent privat, mitfi sakemm tkun lest. Ipprovah, imbagħad ixgħlu.',
  agentDraftCreated: 'L-aġent “{name}” inħoloq, mitfi. Ipprovah, imbagħad ixgħlu.',
  agentDraftFailed: 'Ma setax jinħoloq l-aġent. Erġa’ pprova.',
  scheduleStartsWhenOn: 'jibda meta jinxtegħel',
};

const nl: Copy = {
  heading: 'Begin met een sjabloon',
  description:
    'Elk sjabloon wordt een privéconcept van een Skill. Pas het aan in de chat en publiceer het daarna.',
  useTemplate: 'Sjabloon gebruiken: {name}',
  creating: 'Concept maken…',
  needs: 'Vereist {apps}',
  uses: 'Gebruikt {apps}',
  draftCreated: 'Skill-concept ‘{name}’ gemaakt. Pas het aan in de chat en publiceer het daarna.',
  draftFailed: 'Het Skill-concept kon niet worden gemaakt. Probeer het opnieuw.',
  agentDescription:
    'Elk sjabloon wordt een privéagent die uit staat tot je er klaar voor bent. Probeer hem uit en zet hem daarna aan.',
  agentDraftCreated:
    'Agent ‘{name}’ is gemaakt en staat uit. Probeer hem uit en zet hem daarna aan.',
  agentDraftFailed: 'De agent kon niet worden gemaakt. Probeer het opnieuw.',
  scheduleStartsWhenOn: 'start zodra hij aan staat',
};

const pl: Copy = {
  heading: 'Zacznij od szablonu',
  description:
    'Każdy szablon staje się prywatnym szkicem Skilla. Zmień go w czacie, a potem opublikuj.',
  useTemplate: 'Użyj szablonu: {name}',
  creating: 'Tworzenie szkicu…',
  needs: 'Wymaga: {apps}',
  uses: 'Używa: {apps}',
  draftCreated: 'Utworzono szkic Skilla „{name}”. Zmień go w czacie, a potem opublikuj.',
  draftFailed: 'Nie udało się utworzyć szkicu Skilla. Spróbuj ponownie.',
  agentDescription:
    'Każdy szablon staje się prywatnym agentem, wyłączonym, dopóki nie będziesz gotowy. Wypróbuj go, a potem włącz.',
  agentDraftCreated: 'Utworzono agenta „{name}”, wyłączonego. Wypróbuj go, a potem włącz.',
  agentDraftFailed: 'Nie udało się utworzyć agenta. Spróbuj ponownie.',
  scheduleStartsWhenOn: 'uruchomi się po włączeniu',
};

const pt: Copy = {
  heading: 'Começar a partir de um modelo',
  description:
    'Cada modelo torna-se um rascunho privado de Skill. Altere-o no chat e depois publique-o.',
  useTemplate: 'Usar o modelo: {name}',
  creating: 'A criar o rascunho…',
  needs: 'Requer {apps}',
  uses: 'Usa {apps}',
  draftCreated: 'Rascunho de Skill «{name}» criado. Altere-o no chat e depois publique-o.',
  draftFailed: 'Não foi possível criar o rascunho de Skill. Tente novamente.',
  agentDescription:
    'Cada modelo torna-se um agente privado, desligado até estar pronto. Experimente-o e depois ligue-o.',
  agentDraftCreated: 'Agente «{name}» criado, desligado. Experimente-o e depois ligue-o.',
  agentDraftFailed: 'Não foi possível criar o agente. Tente novamente.',
  scheduleStartsWhenOn: 'começa quando ligado',
};

const ro: Copy = {
  heading: 'Începe de la un șablon',
  description: 'Fiecare șablon devine o schiță privată de Skill. Modific-o în chat, apoi public-o.',
  useTemplate: 'Folosește șablonul: {name}',
  creating: 'Se creează schița…',
  needs: 'Necesită {apps}',
  uses: 'Folosește {apps}',
  draftCreated: 'Schița de Skill „{name}” a fost creată. Modific-o în chat, apoi public-o.',
  draftFailed: 'Schița de Skill nu a putut fi creată. Încearcă din nou.',
  agentDescription:
    'Fiecare șablon devine un agent privat, oprit până când ești pregătit. Încearcă-l, apoi pornește-l.',
  agentDraftCreated: 'Agentul „{name}” a fost creat, oprit. Încearcă-l, apoi pornește-l.',
  agentDraftFailed: 'Agentul nu a putut fi creat. Încearcă din nou.',
  scheduleStartsWhenOn: 'pornește când e activat',
};

const sk: Copy = {
  heading: 'Začnite zo šablóny',
  description:
    'Z každej šablóny vznikne súkromný koncept Skillu. Upravte ho v chate a potom zverejnite.',
  useTemplate: 'Použiť šablónu: {name}',
  creating: 'Vytvára sa koncept…',
  needs: 'Vyžaduje {apps}',
  uses: 'Používa {apps}',
  draftCreated: 'Koncept Skillu „{name}“ bol vytvorený. Upravte ho v chate a potom zverejnite.',
  draftFailed: 'Koncept Skillu sa nepodarilo vytvoriť. Skúste to znova.',
  agentDescription:
    'Z každej šablóny vznikne súkromný agent, vypnutý, kým nebudete pripravení. Vyskúšajte ho a potom ho zapnite.',
  agentDraftCreated: 'Agent „{name}“ bol vytvorený a je vypnutý. Vyskúšajte ho a potom ho zapnite.',
  agentDraftFailed: 'Agenta sa nepodarilo vytvoriť. Skúste to znova.',
  scheduleStartsWhenOn: 'spustí sa po zapnutí',
};

const sl: Copy = {
  heading: 'Začnite s predlogo',
  description:
    'Vsaka predloga postane zasebni osnutek Skilla. Spremenite ga v klepetu in ga nato objavite.',
  useTemplate: 'Uporabi predlogo: {name}',
  creating: 'Ustvarjanje osnutka…',
  needs: 'Potrebuje: {apps}',
  uses: 'Uporablja: {apps}',
  draftCreated:
    'Osnutek Skilla »{name}« je ustvarjen. Spremenite ga v klepetu in ga nato objavite.',
  draftFailed: 'Osnutka Skilla ni bilo mogoče ustvariti. Poskusite znova.',
  agentDescription:
    'Vsaka predloga postane zasebni agent, izklopljen, dokler niste pripravljeni. Preizkusite ga in ga nato vklopite.',
  agentDraftCreated: 'Agent »{name}« je ustvarjen, izklopljen. Preizkusite ga in ga nato vklopite.',
  agentDraftFailed: 'Agenta ni bilo mogoče ustvariti. Poskusite znova.',
  scheduleStartsWhenOn: 'zažene se, ko je vklopljen',
};

const sv: Copy = {
  heading: 'Börja från en mall',
  description: 'Varje mall blir ett privat Skill-utkast. Ändra det i chatten och publicera sedan.',
  useTemplate: 'Använd mallen: {name}',
  creating: 'Skapar utkast…',
  needs: 'Kräver {apps}',
  uses: 'Använder {apps}',
  draftCreated: 'Skill-utkastet ”{name}” har skapats. Ändra det i chatten och publicera sedan.',
  draftFailed: 'Det gick inte att skapa Skill-utkastet. Försök igen.',
  agentDescription:
    'Varje mall blir en privat agent som är avstängd tills du är redo. Testa den och slå sedan på den.',
  agentDraftCreated:
    'Agenten ”{name}” har skapats och är avstängd. Testa den och slå sedan på den.',
  agentDraftFailed: 'Det gick inte att skapa agenten. Försök igen.',
  scheduleStartsWhenOn: 'startar när den slås på',
};

export const templatesCopy: NamespaceCatalog<TemplatesCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default templatesCopy;
